import React, { useEffect, useMemo, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useSelector } from "react-redux/es/hooks/useSelector";
import axios from "axios";
import DocViewer from "@cyntler/react-doc-viewer";
import { jsPDF } from "jspdf";
import "jspdf-autotable";
import { Close, Download, VisibilityOutlined } from "@mui/icons-material";
import { Box, Checkbox, Fade, Modal, Typography, useMediaQuery } from "@mui/material";
import { useTheme } from "@mui/material/styles";
import { saveAs } from "file-saver";

import { API_ENDPOINT } from "../../../services/api/resources";
import Attachment from "../../../components/common/Attachment/Attachment";
import AttachmentList from "../../../components/common/AttachmentList/AttachmentList";
import DataTable from "../../../components/common/DataTable/DataTableSorting";
import TextArea from "../../../components/common/TextArea/TextArea";
import DButton from "../../../components/Dashboard/DashboardButtons/Dbutton";
import DButtonOutlined from "../../../components/Dashboard/DashboardButtons/dButtonOutlined";
import PDFView from "../../../components/Dashboard/PDFView/PDFView";
import HeaderTitle from "../../../components/common/HeaderTitle/HeaderTitle";
import PopupFrame from "../../../components/Popups/PopupFrame/PopupFrame";
import { normalizeVendorsForMessageSelection } from "../../../utils/normalizeVendors";
import brandLogo from "../../../assets/images/png/Logo@2x.png";
import classes from "./RFQTab.module.css";

const formatPrice = (value) => {
  const num = Number(value);
  if (!Number.isFinite(num)) return "-";
  return `Rs. ${num.toLocaleString(undefined, { maximumFractionDigits: 2 })}`;
};

const formatPlainNumber = (value) => {
  const num = Number(value);
  if (!Number.isFinite(num)) return "-";
  return num.toLocaleString(undefined, { maximumFractionDigits: 2 });
};

const isBlankValue = (value) => value === null || value === undefined || value === "";

const toFiniteNumber = (value) => {
  const num = Number(String(value ?? "").replace(/,/g, "").trim());
  return Number.isFinite(num) ? num : null;
};

const getItemQuantity = (item = {}) => toFiniteNumber(item?.quantity) ?? 1;

const getItemDiscount = (item = {}) =>
  toFiniteNumber(item?.discount_amount ?? item?.discountAmount ?? item?.discount) ?? 0;

const getItemTaxPercent = (item = {}) => toFiniteNumber(item?.tax ?? item?.tax_percent ?? item?.taxPercentage);

const getItemUnitPrice = (item = {}) => {
  const unitPrice = item?.unit_price ?? item?.unitPrice;
  if (!isBlankValue(unitPrice)) return unitPrice;
  return item?.total_price ?? item?.totalPrice ?? item?.total_amount ?? item?.totalAmount;
};

const getItemTaxableAmount = (item = {}) => {
  const taxableAmount = item?.taxable_amount ?? item?.taxableAmount;
  if (!isBlankValue(taxableAmount)) return taxableAmount;

  const unitPrice = toFiniteNumber(getItemUnitPrice(item));
  if (unitPrice === null) return null;
  return Math.max(0, unitPrice * getItemQuantity(item) - getItemDiscount(item));
};

const getItemTotalAmount = (item = {}) => {
  const totalAmount = item?.total_price ?? item?.totalPrice ?? item?.total_amount ?? item?.totalAmount;
  if (!isBlankValue(totalAmount)) return totalAmount;

  const taxableAmount = toFiniteNumber(getItemTaxableAmount(item));
  if (taxableAmount === null) return null;
  const taxPercent = getItemTaxPercent(item) ?? 0;
  return taxableAmount + (taxableAmount * taxPercent) / 100;
};

const formatTaxPercent = (value) => {
  const tax = toFiniteNumber(value);
  return tax === null ? "-" : `${formatPlainNumber(tax)}%`;
};

const formatOptionalPrice = (value) => (isBlankValue(value) ? "-" : formatPrice(value));

const formatQuantityWithUnit = (item = {}) => {
  const quantity = item?.quantity ?? "";
  const unit = String(item?.unit || item?.uom || "").trim();
  const formattedQuantity =
    quantity === null || quantity === undefined || quantity === ""
      ? "-"
      : formatPlainNumber(quantity);

  if (formattedQuantity === "-") return unit || "-";
  return unit ? `${formattedQuantity} ${unit}` : formattedQuantity;
};

const formatDateTime = (value) => {
  if (!value) return "-";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return String(value);
  return date.toLocaleString(undefined, {
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
  });
};

const formatDateOnly = (value) => {
  if (!value) return "-";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return String(value);
  return date.toLocaleDateString(undefined, {
    day: "2-digit",
    month: "long",
    year: "numeric",
  });
};

const formatTimeOnly = (value) => {
  if (!value) return "-";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return String(value);
  return date.toLocaleTimeString(undefined, {
    hour: "2-digit",
    minute: "2-digit",
  });
};

const formatDisplayDateTime = (value) => {
  if (!value) return "-";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return String(value);
  return `${date.toLocaleDateString(undefined, {
    day: "2-digit",
    month: "short",
    year: "numeric",
  })}, ${date.toLocaleTimeString(undefined, {
    hour: "2-digit",
    minute: "2-digit",
  })}`;
};

const normalizeVendorQuotationTokens = (detail) => {
  const token1 = detail?.vendor_quotation_token;
  const token2 = detail?.vendor_quotation_tokens;

  return [
    ...(Array.isArray(token1) ? token1 : token1 ? [token1] : []),
    ...(Array.isArray(token2) ? token2 : token2 ? [token2] : []),
  ]
    .map((token) => String(token || "").trim())
    .filter(Boolean);
};

const normalizeAttachmentEntry = (doc, index) => {
  if (!doc) return null;
  if (typeof doc === "string") {
    return {
      url: getCloudFrontFileUrl(doc),
      name: doc.split("/").pop() || `Attachment ${index + 1}`,
    };
  }

  const url = doc.url || doc.uri || doc.document || doc.file || "";
  if (!url) return null;
  return {
    url: getCloudFrontFileUrl(url),
    name: doc.name || url.split("/").pop() || `Attachment ${index + 1}`,
  };
};

const normalizeChatAttachmentEntry = (doc, index) => {
  if (!doc) return null;

  if (typeof doc === "string") {
    return {
      url: getCloudFrontFileUrl(doc),
      name: doc.split("/").pop() || `Attachment ${index + 1}`,
    };
  }

  const url = doc.attachment_url || doc.attachment || doc.url || doc.uri || "";
  if (!url) return null;

  return {
    url: getCloudFrontFileUrl(url),
    name:
      doc.attachment_name ||
      doc.name ||
      String(url).split("/").pop() ||
      `Attachment ${index + 1}`,
  };
};

const parseTimelineDate = (value) => {
  if (!value || typeof value !== "string") return new Date(0);
  try {
    if (value.includes(" - ")) {
      const [datePart, timePart] = value.split(" - ");
      return new Date(`${datePart.trim()} ${timePart.trim()}`);
    }
    return new Date(value.trim());
  } catch (error) {
    return new Date(0);
  }
};

const getCloudFrontFileUrl = (fileUrl) => {
  const rawUrl = String(fileUrl || "").trim();
  if (!rawUrl) return "";

  const cloudFrontUrl = String(process.env.REACT_APP_CLOUDFRONT_URL || "")
    .trim()
    .replace(/\/+$/, "");

  if (!cloudFrontUrl) return rawUrl;

  if (rawUrl.startsWith("blob:") || rawUrl.startsWith("data:")) {
    return rawUrl;
  }

  if (rawUrl.startsWith(cloudFrontUrl)) {
    return rawUrl;
  }

  try {
    const parsedUrl = new URL(rawUrl);
    return `${cloudFrontUrl}${parsedUrl.pathname}${parsedUrl.search || ""}`;
  } catch (error) {
    const cleanPath = rawUrl.startsWith("/") ? rawUrl : `/${rawUrl}`;
    return `${cloudFrontUrl}${cleanPath}`;
  }
};

const downloadFileFromUrl = async (fileUrl, fileName = "quotation-file") => {
  const url = getCloudFrontFileUrl(fileUrl);
  if (!url) return;

  const response = await axios.get(url, { responseType: "blob" });
  saveAs(response.data, fileName);
};

const createFreeRfqManualQuotationPdfFile = async (quote = {}) => {
  const rfqCode = quote?.rfq_code || quote?.rfq_details?.rfq_code || "RFQ";
  const vendorEmail = quote?.vendor_email || "-";
  const vendorName =
    quote?.vendor_name ||
    quote?.vendor_contact_name ||
    quote?.company_name ||
    vendorEmail;
  const currencyType = quote?.quotation_details?.currency || "INR";
  const currencyValue = quote?.quotation_details?.exchange_rate_to_inr ?? 1;
  const convertedCurrency = quote?.quotation_details?.invoice_grand_total ?? 0;
  const inrValue =
    Number(currencyValue || 0) * Number(convertedCurrency || 0) ||
    convertedCurrency ||
    0;
  const quoteItems = Array.isArray(quote?.quotation_details?.items)
    ? quote.quotation_details.items
    : [];

  const formatNumber = (value) => {
    const num = Number(String(value ?? "").replace(/,/g, "").trim());
    if (!Number.isFinite(num)) return "-";
    return num.toLocaleString("en-IN", { maximumFractionDigits: 2 });
  };

  const safeText = (value) => String(value ?? "").trim() || "-";
  const safeRfq = String(rfqCode || "RFQ").replace(/[\\/:*?"<>|]/g, "_");
  const fileName = `${safeRfq}_QuotationSummary.pdf`;
  const nowStr = new Date(
    quote?.updated_at || quote?.created_at || Date.now(),
  ).toLocaleString("en-IN");

  const pdf = new jsPDF({ unit: "pt", format: "a4", orientation: "portrait" });
  const pageWidth = pdf.internal.pageSize.getWidth();
  const marginX = 40;

  let y = 48;
  let titleStartX = marginX;

  try {
    const logoW = 88;
    const logoH = 28;
    pdf.addImage(brandLogo, "png", marginX, 22, logoW, logoH);
    y = 46;
    titleStartX = marginX + logoW + 12;
  } catch (error) {
    // Continue without logo if rendering fails.
  }

  pdf.setFont("helvetica", "bold");
  pdf.setFontSize(16);
  pdf.text("Quotation Submission Details", titleStartX, y);
  pdf.setFont("helvetica", "normal");
  pdf.setFontSize(10);
  pdf.setTextColor(71, 85, 105);
  pdf.text(`Generated On: ${nowStr}`, pageWidth - marginX, y, {
    align: "right",
  });
  pdf.setTextColor(15, 23, 42);
  y += 18;

  pdf.setDrawColor(226, 232, 240);
  pdf.line(marginX, y, pageWidth - marginX, y);
  y += 18;

  const submittedBy = vendorName
    ? `${safeText(vendorName)} <${safeText(vendorEmail)}>`
    : safeText(vendorEmail);

  pdf.autoTable({
    theme: "plain",
    startY: y,
    margin: { left: marginX, right: marginX },
    tableWidth: pageWidth - marginX * 2,
    body: [
      ["RFQ ID", safeText(rfqCode), "Currency", safeText(currencyType)],
      [
        "Submitted By",
        submittedBy,
        "Buyer",
        safeText(quote?.buyer_name || quote?.buyer_email),
      ],
    ],
    styles: {
      fontSize: 10.5,
      cellPadding: { top: 2, right: 6, bottom: 2, left: 0 },
      overflow: "linebreak",
    },
    columnStyles: {
      0: { cellWidth: 78, textColor: [71, 85, 105], fontStyle: "bold" },
      1: { cellWidth: 210, textColor: [15, 23, 42] },
      2: { cellWidth: 78, textColor: [71, 85, 105], fontStyle: "bold" },
      3: { cellWidth: "auto", textColor: [15, 23, 42] },
    },
  });

  y = (pdf.lastAutoTable?.finalY || y + 40) + 14;

  const head = [[
    "Item Code",
    "Item Name",
    "Quantity",
    "Unit",
    "Unit Price",
    "Total Price",
    "Description",
  ]];
  const body = quoteItems.length
    ? quoteItems.map((item) => [
        safeText(item?.item_code || item?.itemCode),
        safeText(item?.item_name || item?.itemName),
        formatNumber(item?.quantity),
        safeText(item?.unit),
        currencyType
          ? `${safeText(currencyType)} ${formatNumber(item?.unit_price || item?.unitPrice)}`
          : formatNumber(item?.unit_price || item?.unitPrice),
        currencyType
          ? `${safeText(currencyType)} ${formatNumber(item?.total_price || item?.totalPrice)}`
          : formatNumber(item?.total_price || item?.totalPrice),
        safeText(item?.description),
      ])
    : [["-", "-", "-", "-", "-", "-", "-"]];

  pdf.autoTable({
    theme: "grid",
    startY: y,
    head,
    body,
    margin: { left: marginX, right: marginX },
    headStyles: {
      fillColor: [59, 134, 199],
      textColor: [255, 255, 255],
      fontStyle: "bold",
      halign: "center",
    },
    styles: {
      fontSize: 9,
      cellPadding: 4,
      overflow: "linebreak",
      valign: "top",
    },
    columnStyles: {
      0: { cellWidth: 62 },
      1: { cellWidth: 118 },
      2: { cellWidth: 52, halign: "right" },
      3: { cellWidth: 44 },
      4: { cellWidth: 68, halign: "right" },
      5: { cellWidth: 72, halign: "right" },
      6: { cellWidth: "auto" },
    },
  });

  const footerY = (pdf.lastAutoTable?.finalY || y + 120) + 14;
  pdf.autoTable({
    theme: "grid",
    startY: footerY,
    margin: { left: marginX, right: marginX },
    head: [["Currency Value", "Converted", "INR"]],
    body: [[
      formatNumber(currencyValue),
      formatNumber(convertedCurrency),
      formatNumber(inrValue),
    ]],
    headStyles: {
      fillColor: [241, 245, 249],
      textColor: [71, 85, 105],
      fontStyle: "bold",
      halign: "center",
    },
    styles: {
      fontSize: 9.5,
      cellPadding: 4,
      halign: "center",
      overflow: "linebreak",
    },
    columnStyles: {
      0: { cellWidth: (pageWidth - marginX * 2) / 3 },
      1: { cellWidth: (pageWidth - marginX * 2) / 3 },
      2: { cellWidth: (pageWidth - marginX * 2) / 3 },
    },
  });

  const blob = pdf.output("blob");
  return new File([blob], fileName, { type: "application/pdf" });
};

const FreeRFQDetails = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const theme = useTheme();
  const isMobileView = useMediaQuery(theme.breakpoints.down("md"));
  const loginData = useSelector((state) => state.loginData.value);
  const buyerToken = loginData?.user?.token;
  const authToken = loginData?.auth_token;
  const mobileNumber = loginData?.user?.phone_number || "";

  const [loading, setLoading] = useState(false);
  const [vendorQuotationLoading, setVendorQuotationLoading] = useState(false);
  const [freeRfq, setFreeRfq] = useState(null);
  const [vendorQuotations, setVendorQuotations] = useState([]);
  const [buyerChatThreads, setBuyerChatThreads] = useState([]);
  const [expandedDescriptions, setExpandedDescriptions] = useState({});

  const [actionMode, setActionMode] = useState("");
  const [quoteSelectionKey, setQuoteSelectionKey] = useState(0);
  const [selectedQuoteTokens, setSelectedQuoteTokens] = useState([]);
  const [selectedQuoteRows, setSelectedQuoteRows] = useState([]);
  const [openViewQuotesPopup, setOpenViewQuotesPopup] = useState(false);
  const [viewQuoteItemsRow, setViewQuoteItemsRow] = useState(null);

  const [sendMessage, setSendMessage] = useState(false);
  const [textAreaSendMessage, setTextAreaSendMessage] = useState(false);
  const [sendErrorMsg, setSendErrorMsg] = useState(false);
  const [errorMessages, setErrorMessages] = useState(false);
  const [charsLeft, setCharsLeft] = useState(0);
  const [msgFile, setMsgFile] = useState([]);
  const [sendingMessage, setSendingMessage] = useState(false);
  const [selectedVendorTokens, setSelectedVendorTokens] = useState([]);
  const [selectedVendorRows, setSelectedVendorRows] = useState([]);
  const [textAreaValue, setTextAreaValue] = useState({ send_message: "" });

  const [infoPopup, setInfoPopup] = useState({
    open: false,
    title: "",
    description: "",
  });

  const [viewTimeLine, setViewTimeLine] = useState(false);
  const [timeLineVendorData, setTimeLineVendorData] = useState(null);
  const [viewTimeLineData, setViewTimeLineData] = useState([]);
  const [timelineLoading, setTimelineLoading] = useState(false);
  const [timelineError, setTimelineError] = useState("");
  const [chatClosed, setChatClosed] = useState(false);
  const [chatStatusLoading, setChatStatusLoading] = useState(false);

  const vendorQuoteSectionRef = useRef(null);
  const vendorMessageSectionRef = useRef(null);
  const autoOpenTimelineRef = useRef(false);

  const rfqToken = useMemo(() => {
    const params = new URLSearchParams(location.search || "");
    return String(params.get("rfq_token") || "").trim();
  }, [location.search]);

  const preloadedFreeRfq = useMemo(() => {
    if (location?.state?.freeRfq?.rfq_token) {
      return location.state.freeRfq;
    }
    return null;
  }, [location.state]);

  const isLinkFlow = useMemo(() => !authToken, [authToken]);
  const effectiveRfqToken = String(freeRfq?.rfq_token || rfqToken || "").trim();

  useEffect(() => {
    if (preloadedFreeRfq?.rfq_token) {
      setFreeRfq(preloadedFreeRfq);
      setLoading(false);
      return;
    }

    const load = async () => {
      if (!rfqToken) return;
      setLoading(true);
      try {
        const detailResponse = await axios.post(
          API_ENDPOINT.get_free_rfq_detail,
          { rfq_token: rfqToken },
          authToken
            ? { headers: { Authorization: `Bearer ${authToken}` } }
            : undefined,
        );

        const detail = detailResponse?.data?.data || detailResponse?.data || {};
        setFreeRfq(detail || null);
      } catch (error) {
        console.error("Error loading Free RFQ details:", error);
        setFreeRfq(null);
        setVendorQuotations([]);
      } finally {
        setLoading(false);
      }
    };

    load();
  }, [authToken, preloadedFreeRfq, rfqToken]);

  useEffect(() => {
    if (!freeRfq) return;

    if (
      Array.isArray(freeRfq?.vendor_submitted_details) &&
      freeRfq.vendor_submitted_details.length > 0
    ) {
      setVendorQuotations(freeRfq.vendor_submitted_details);
      setVendorQuotationLoading(false);
      return;
    }

    const tokens = normalizeVendorQuotationTokens(freeRfq);
    if (!tokens.length) {
      setVendorQuotations([]);
      return;
    }

    const loadVendorQuotes = async () => {
      setVendorQuotationLoading(true);
      try {
        const responses = await Promise.all(
          tokens.map((vendorQuotationToken) => {
            if (isLinkFlow) {
              return axios.post(
                API_ENDPOINT.get_free_rfq_vendor_quotation_link,
                { vendor_quotation_token: vendorQuotationToken },
              );
            }

            return axios.post(
              API_ENDPOINT.get_free_rfq_vendor_quotation,
              { vendor_quotation_token: vendorQuotationToken },
              { headers: { Authorization: `Bearer ${authToken}` } },
            );
          }),
        );

        const combined = responses.flatMap((response) => {
          if (Array.isArray(response?.data?.results))
            return response.data.results;
          if (response?.data?.vendor_quotation_token) return [response.data];
          return [];
        });

        setVendorQuotations(combined);
      } catch (error) {
        console.error("Error loading vendor quotations:", error);
        setVendorQuotations([]);
      } finally {
        setVendorQuotationLoading(false);
      }
    };

    loadVendorQuotes();
  }, [authToken, freeRfq, isLinkFlow]);

  useEffect(() => {
    if (!effectiveRfqToken || !buyerToken || !authToken) {
      setBuyerChatThreads([]);
      return;
    }

    const loadBuyerChatThreads = async () => {
      try {
        const response = await axios.post(
          API_ENDPOINT.get_free_rfq_buyer_chat_threads,
          {
            buyer_token: buyerToken,
            rfq_token: effectiveRfqToken,
          },
          {
            headers: {
              Authorization: `Bearer ${authToken}`,
            },
          },
        );

        setBuyerChatThreads(
          Array.isArray(response?.data?.results) ? response.data.results : [],
        );
      } catch (error) {
        console.error("Error loading easyRFQ buyer chat threads:", error);
        setBuyerChatThreads([]);
      }
    };

    loadBuyerChatThreads();
  }, [authToken, buyerToken, effectiveRfqToken]);

  const toggleDescription = (key) => {
    setExpandedDescriptions((prev) => ({ ...prev, [key]: !prev[key] }));
  };

  const renderDescriptionCell = (description, key) => {
    const text = String(description || "-");
    const expanded = Boolean(expandedDescriptions[key]);
    const truncated = text.length > 90;
    const shownText = !truncated || expanded ? text : `${text.slice(0, 90)}...`;

    return (
      <div style={{ whiteSpace: "pre-wrap", wordBreak: "break-word" }}>
        <span>{shownText}</span>
        {truncated ? (
          <button
            type="button"
            onClick={() => toggleDescription(key)}
            style={{
              display: "block",
              marginTop: 6,
              padding: 0,
              border: "none",
              background: "transparent",
              color: "#2563eb",
              cursor: "pointer",
              fontSize: 14,
              fontFamily: "gilroyMedium",
            }}
          >
            {expanded ? "Read less" : "Read more"}
          </button>
        ) : null}
      </div>
    );
  };

  const items = Array.isArray(freeRfq?.items) ? freeRfq.items : [];
  const vendorQuotationTokens = normalizeVendorQuotationTokens(freeRfq);

  const vendorMessageHeadings = useMemo(
    () => [
      { label: "Supplier Name", field: "first_name", hasCheckbox: false },

      { label: "Supplier Email", field: "company_official_email" },
      { label: "Supplier Phone Number", field: "company_phone_number" },
    ],
    [],
  );

  const headingMessage = useMemo(
    () => [{ label: "Supplier Name", field: "company_name" }],
    [],
  );

  const quoteTableHeadings = useMemo(
    () => [
      { label: "Supplier Email", field: "vendor", hasCheckbox: true },
      { label: "Quantity", field: "quantity" },
      { label: "Unit Price", field: "unit_price" },
      { label: "Total Price in Global Currency", field: "total_price" },
      { label: "Total Price in Local Currency", field: "converted_currency" },
      { label: "Quote Iteration", field: "bid_version" },
      { label: "Created Date", field: "created_date" },
      { label: "File", field: "file_url" },
      { label: "View Item", field: "view_items" },
    ],
    [],
  );

  const vendorMessagesTableData = useMemo(() => {
    const fallbackEmails = Array.isArray(freeRfq?.vendor_emails)
      ? freeRfq.vendor_emails
      : [];
    const fallbackMobiles = Array.isArray(freeRfq?.vendor_mobile_numbers)
      ? freeRfq.vendor_mobile_numbers
      : [];
    const vendorMap = new Map();
    const threadList = Array.isArray(buyerChatThreads) ? buyerChatThreads : [];

    const buildDisplayName = (value, fallbackEmail, index) => {
      const normalized = String(value || "").trim();
      if (normalized) return normalized;
      if (fallbackEmail && String(fallbackEmail).includes("@")) {
        return String(fallbackEmail).split("@")[0];
      }
      return `Supplier ${index + 1}`;
    };

    vendorQuotations.forEach((quote, index) => {
      const email =
        quote?.vendor_email ||
        quote?.company_official_email ||
        fallbackEmails[index] ||
        "-";
      const phone =
        quote?.vendor_mobile ||
        quote?.company_phone_number ||
        fallbackMobiles[index] ||
        "-";
      const token = String(email || `vendor-${index}`)
        .trim()
        .toLowerCase();

      if (!token) return;

      const existing = vendorMap.get(token);
      const matchingThread =
        threadList.find(
          (thread) =>
            String(thread?.vendor_email || "")
              .trim()
              .toLowerCase() ===
            String(email || "")
              .trim()
              .toLowerCase(),
        ) ||
        threadList.find(
          (thread) =>
            String(thread?.vendor_mobile || "").trim() &&
            String(thread?.vendor_mobile || "").trim() ===
              String(phone || "").trim(),
        );

      vendorMap.set(token, {
        token,
        vendor_token: String(
          quote?.vendor_token ||
            quote?.token ||
            quote?.vendor_contact_token ||
            token,
        ).trim(),
        vendor_contact_token: String(quote?.vendor_contact_token || "").trim(),
        first_name: buildDisplayName(
          quote?.vendor_name ||
            quote?.vendor_contact_name ||
            quote?.company_name ||
            quote?.quotation_details?.vendor_name,
          email,
          index,
        ),
        company_name: buildDisplayName(
          quote?.vendor_name ||
            quote?.vendor_contact_name ||
            quote?.company_name ||
            quote?.quotation_details?.vendor_name,
          email,
          index,
        ),
        vendor_code:
          quote?.vendor_code ||
          quote?.supplier_id ||
          quote?.quotation_details?.vendor_code ||
          existing?.vendor_code ||
          "-",
        company_official_email: email,
        company_phone_number: phone,
        quote_token: String(
          quote?.vendor_quotation_token || existing?.quote_token || "",
        ).trim(),
        thread_token: String(
          matchingThread?.thread_token || existing?.thread_token || "",
        ).trim(),
        participant_token: String(
          matchingThread?.participant_token ||
            existing?.participant_token ||
            "",
        ).trim(),
        unread_count: Number(
          matchingThread?.unread_count || existing?.unread_count || 0,
        ),
        chat_is_closed: Boolean(
          matchingThread?.chat_is_closed ?? existing?.chat_is_closed ?? false,
        ),
        last_message:
          matchingThread?.last_message || existing?.last_message || null,
        role: "vendor contact",
        chat: Array.isArray(quote?.chat) ? quote.chat : existing?.chat || [],
      });
    });

    if (vendorMap.size === 0) {
      fallbackEmails.forEach((email, index) => {
        const normalizedEmail = String(email || "").trim() || "-";
        const token = `vendor-${index}`;
        const matchingThread =
          threadList.find(
            (thread) =>
              String(thread?.vendor_email || "")
                .trim()
                .toLowerCase() === normalizedEmail.toLowerCase(),
          ) ||
          threadList.find(
            (thread) =>
              String(thread?.vendor_mobile || "").trim() &&
              String(thread?.vendor_mobile || "").trim() ===
                String(fallbackMobiles[index] || "").trim(),
          );
        vendorMap.set(token, {
          token,
          vendor_token: token,
          vendor_contact_token: "",
          first_name: buildDisplayName("", normalizedEmail, index),
          company_name: buildDisplayName("", normalizedEmail, index),
          vendor_code: "-",
          company_official_email: normalizedEmail,
          company_phone_number: fallbackMobiles[index] || "-",
          quote_token: "",
          thread_token: String(matchingThread?.thread_token || "").trim(),
          participant_token: String(
            matchingThread?.participant_token || "",
          ).trim(),
          unread_count: Number(matchingThread?.unread_count || 0),
          chat_is_closed: Boolean(matchingThread?.chat_is_closed),
          last_message: matchingThread?.last_message || null,
          role: "vendor contact",
          chat: [],
        });
      });
    }

    return Array.from(vendorMap.values());
  }, [
    buyerChatThreads,
    freeRfq?.vendor_emails,
    freeRfq?.vendor_mobile_numbers,
    vendorQuotations,
  ]);

  const messageSelectionTableData = useMemo(
    () => normalizeVendorsForMessageSelection(vendorMessagesTableData),
    [vendorMessagesTableData],
  );

  useEffect(() => {
    if (!viewTimeLine || autoOpenTimelineRef.current) return;
    if (!vendorMessagesTableData.length) return;

    autoOpenTimelineRef.current = true;
    void fetchVendorTimeline(vendorMessagesTableData[0]);
  }, [vendorMessagesTableData, viewTimeLine]);

  const handleManualQuotationPdfDownload = async (quote) => {
    try {
      const pdfFile = await createFreeRfqManualQuotationPdfFile(quote || {});
      saveAs(pdfFile, pdfFile.name);
    } catch (error) {
      console.error("Failed to generate manual quotation PDF:", error);
    }
  };

  const openQuoteItemsModal = (row) => {
    setViewQuoteItemsRow(row || null);
  };

  const closeQuoteItemsModal = () => {
    setViewQuoteItemsRow(null);
  };

  const renderQuoteItemsLink = (row, fallbackValue) => {
    const itemCount = Array.isArray(row?.quoteItems) ? row.quoteItems.length : 0;
    if (itemCount <= 1) return fallbackValue || "-";

    return (
      <button
        type="button"
        className={classes.quoteItemsLink}
        onClick={() => openQuoteItemsModal(row)}
      >
        Multiple Items
      </button>
    );
  };

  const renderReadMoreText = (value, className = "", maxLength = 60) => {
    const text = String(value ?? "").trim();
    if (!text) return "-";

    if (text.length <= maxLength) {
      return (
        <span className={classes.quoteItemsNoWrapCell} title={text}>
          {text}
        </span>
      );
    }

    return (
      <details className={`${classes.readMoreCell} ${className}`}>
        <summary title={text}>
          <span>{text}</span>
        </summary>
        <div title={text}>{text}</div>
      </details>
    );
  };

const quoteTableData = useMemo(() => {
  return vendorQuotations.map((quote, index) => {
    const quoteItems = Array.isArray(quote?.quotation_details?.items)
      ? quote.quotation_details.items
      : [];

    const totalQuantity = quoteItems.reduce(
      (sum, item) => sum + Number(item?.quantity || 0),
      0,
    );

    const totalPrice = quoteItems.reduce(
      (sum, item) =>
        sum + Number(getItemTotalAmount(item) ?? getItemTaxableAmount(item) ?? 0),
      0,
    );

    const firstItem = quoteItems[0] || {};
    const singleQuantity = quoteItems.length === 1 ? formatQuantityWithUnit(firstItem) : "";
    const unitPrice =
      !isBlankValue(getItemUnitPrice(firstItem))
        ? Number(getItemUnitPrice(firstItem))
        : totalQuantity > 0 && totalPrice > 0
          ? totalPrice / totalQuantity
          : null;

    const documentUrl =
      quote?.file_url ||
      quote?.document ||
      quote?.document_url ||
      quote?.quotation_document ||
      quote?.quotation_details?.document ||
      "";

    const documentName =
      quote?.file_name ||
      (documentUrl ? String(documentUrl).split("/").pop() : "") ||
      "quotation.pdf";

    const fileUrl = documentUrl ? getCloudFrontFileUrl(documentUrl) : "";
    const manualPdfAvailable = !fileUrl && quoteItems.length > 0;

    return {
      token: String(quote?.vendor_quotation_token || `quote-${index}`).trim(),
      vendor_token: String(quote?.vendor_email || `vendor-${index}`).trim(),
      vendor_contact_token: String(quote?.vendor_contact_token || "").trim(),
      quote_token: String(quote?.vendor_quotation_token || "").trim(),

      vendor:
        quote?.vendor_name ||
        quote?.vendor_contact_name ||
        quote?.company_name ||
        quote?.vendor_email ||
        `Supplier ${index + 1}`,

      vendor_email: quote?.vendor_email || "-",
      vendor_code: quote?.vendor_code || quote?.supplier_id || "-",

      quantity: renderQuoteItemsLink(
        { quoteItems, rawQuote: quote },
        singleQuantity || (totalQuantity ? formatPlainNumber(totalQuantity) : "-"),
      ),
      unit_price: renderQuoteItemsLink(
        { quoteItems, rawQuote: quote },
        unitPrice != null ? formatPrice(unitPrice) : "-",
      ),
      total_price: totalPrice ? formatPrice(totalPrice) : "-",
      converted_currency: totalPrice ? formatPrice(totalPrice) : "-",

      bid_version: `Submission ${vendorQuotations.length - index}`,
      bid_version_int: vendorQuotations.length - index,

      created_date: formatDisplayDateTime(quote?.updated_at || quote?.created_at),

      document: documentUrl || null,
      documentUrl: fileUrl,
      documentName,

      rawQuote: quote,
      rawQuotes: [quote],
      quoteItems,

      file_url: fileUrl ? (
        <button
          type="button"
          className={classes.fileActionLink}
          onClick={() => downloadFileFromUrl(fileUrl, documentName)}
          title={fileUrl}
        >
          <span>{documentName}</span>
        </button>
      ) : manualPdfAvailable ? (
        <button
          type="button"
          className={classes.fileActionLink}
          onClick={() => handleManualQuotationPdfDownload(quote)}
          title="Download quotation summary PDF"
        >
          <span>{documentName || "quotation-summary.pdf"}</span>
        </button>
      ) : (
        "-"
      ),
      view_items: (
        <button
          type="button"
          className={classes.quoteViewIconButton}
          onClick={() => openQuoteItemsModal({ rawQuote: quote, quoteItems })}
          title="View item details"
          aria-label="View item details"
        >
          <VisibilityOutlined sx={{ fontSize: 18 }} />
        </button>
      ),
    };
  });
}, [vendorQuotations]);
  const timelineDocuments = useMemo(() => {
    const documents = [];

    (Array.isArray(viewTimeLineData) ? viewTimeLineData : []).forEach(
      (entry) => {
        const docList = Array.isArray(entry?.document) ? entry.document : [];
        docList.forEach((doc, index) => {
          const normalized = normalizeChatAttachmentEntry(doc, index);
          if (normalized) {
            documents.push({ document: [normalized] });
          }
        });
      },
    );

    if (timeLineVendorData?.document) {
      const normalized = normalizeChatAttachmentEntry(
        timeLineVendorData.document,
        0,
      );
      if (normalized) {
        documents.push({ document: [normalized] });
      }
    }

    return documents;
  }, [timeLineVendorData, viewTimeLineData]);

const selectedQuoteDocs = useMemo(() => {
  return selectedQuoteRows
    .map((row) => {
      const docUrl = row?.documentUrl || row?.document;
      if (!docUrl) return null;

      return [
        {
          uri: getCloudFrontFileUrl(docUrl),
          fileName: row?.documentName || row?.vendor || "Supplier Quote",
        },
      ];
    })
    .filter(Boolean);
}, [selectedQuoteRows]);

  const selectedQuoteDataForViewer = useMemo(() => {
    return selectedQuoteRows.map((row) => row?.rawQuote).filter(Boolean);
  }, [selectedQuoteRows]);

  const selectedQuoteTokenSet = useMemo(
    () => new Set(selectedQuoteTokens),
    [selectedQuoteTokens],
  );

  const viewQuoteItems = useMemo(
    () => (Array.isArray(viewQuoteItemsRow?.quoteItems) ? viewQuoteItemsRow.quoteItems : []),
    [viewQuoteItemsRow],
  );

  const viewQuoteSupplier = useMemo(() => {
    const quote = viewQuoteItemsRow?.rawQuote || {};
    return (
      quote?.vendor_name ||
      quote?.vendor_contact_name ||
      quote?.company_name ||
      quote?.vendor_email ||
      viewQuoteItemsRow?.vendor ||
      "Supplier"
    );
  }, [viewQuoteItemsRow]);

  const transactionDetails = useMemo(
  () => [
    ["RFQ ID", freeRfq?.rfq_code || "-"],
    ["Bid Start Date", formatDateOnly(freeRfq?.bid_from_date)],
    ["Bid End Date", formatDateOnly(freeRfq?.bid_to_date)],
    ["Bid Time", formatTimeOnly(freeRfq?.bid_time)],
    ["Bid Status", freeRfq?.bid_status || "-"],
    [
      "Buyer Company",
      freeRfq?.buyer_contact?.company_name ||
        freeRfq?.buyer_company_name ||
        "-",
    ],
    [
      "Buyer Email",
      freeRfq?.buyer_contact?.email ||
        freeRfq?.buyer_email ||
        "-",
    ],
    [
      "Buyer Mobile",
      mobileNumber || "-",
    ],
    ["Shipping Address 1", freeRfq?.shipping_address1 || "-"],
    ["Shipping Address 2", freeRfq?.shipping_address2 || "-"],
    ["City", freeRfq?.city || "-"],
    ["State", freeRfq?.state || "-"],
    ["Country", freeRfq?.country || "-"],
    ["Pincode", freeRfq?.pincode || "-"],
  ],
  [
    freeRfq?.rfq_code,
    freeRfq?.bid_from_date,
    freeRfq?.bid_to_date,
    freeRfq?.bid_time,
    freeRfq?.bid_status,
    freeRfq?.buyer_company_name,
    freeRfq?.buyer_contact?.company_name,
    freeRfq?.buyer_contact?.email,
    freeRfq?.buyer_email,
    mobileNumber,
    freeRfq?.shipping_address1,
    freeRfq?.shipping_address2,
    freeRfq?.city,
    freeRfq?.state,
    freeRfq?.country,
    freeRfq?.pincode,
  ],
);

  const timelineHeaderVendor =
    timeLineVendorData?.company_name || timeLineVendorData?.first_name || "-";
  const timelineHeaderSupplier = timelineHeaderVendor;

  const scrollToSection = (ref) => {
    if (ref?.current?.scrollIntoView) {
      ref.current.scrollIntoView({ behavior: "smooth", block: "start" });
    }
  };

  const resetActionMode = () => {
    setActionMode("");
    setSelectedQuoteTokens([]);
    setSelectedQuoteRows([]);
    setQuoteSelectionKey((prev) => prev + 1);
    setOpenViewQuotesPopup(false);
  };

  const openInfoPopup = (title, description) => {
    setInfoPopup({
      open: true,
      title,
      description,
    });
  };

  const closeInfoPopup = () => {
    setInfoPopup({
      open: false,
      title: "",
      description: "",
    });
  };

  const handleQuoteSelection = (rowList, rows) => {
    setSelectedQuoteTokens(Array.isArray(rowList) ? rowList : []);
    setSelectedQuoteRows(Array.isArray(rows) ? rows : []);
  };

  const toggleMobileQuoteSelection = (row) => {
    const rowToken = String(row?.token || "").trim();
    if (!rowToken) return;

    setSelectedQuoteRows((prevRows) => {
      const exists = prevRows.some(
        (selectedRow) => String(selectedRow?.token || "").trim() === rowToken,
      );
      const nextRows = exists
        ? prevRows.filter(
            (selectedRow) =>
              String(selectedRow?.token || "").trim() !== rowToken,
          )
        : [...prevRows, row];

      setSelectedQuoteTokens(
        nextRows.map((selectedRow) => String(selectedRow?.token || "").trim()),
      );

      return nextRows;
    });
  };

  const handleVendorSelection = (rowList, rows) => {
    setSelectedVendorTokens(Array.isArray(rowList) ? rowList : []);
    setSelectedVendorRows(Array.isArray(rows) ? rows : []);
  };

  const handleOpenMessageVendor = () => {
    setSendMessage(true);
    setTextAreaSendMessage(false);
    setSendErrorMsg(false);
  };

  const handleCloseMessageVendor = () => {
    setSendMessage(false);
    setTextAreaSendMessage(false);
    setSendErrorMsg(false);
    setErrorMessages(false);
    setSelectedVendorTokens([]);
    setSelectedVendorRows([]);
    setTextAreaValue({ send_message: "" });
    setCharsLeft(0);
    setMsgFile([]);
    setSendingMessage(false);
  };

  const handleContinueMessage = () => {
    if (selectedVendorTokens.length > 0) {
      setSendMessage(false);
      setTextAreaSendMessage(true);
      setSendErrorMsg(false);
      return;
    }

    setSendErrorMsg(true);
  };

  const handleWordCount = (event) => {
    const inputValue = event.target.value;
    const maxChar = 2000;
    const truncatedValue = inputValue.slice(0, maxChar);
    setTextAreaValue({ send_message: truncatedValue });
    setCharsLeft(Math.min(maxChar, inputValue.length));
  };

  const handleFileChange = (event) => {
    const file = event.target.files[0];
    if (file) {
      setMsgFile((prevFiles) => [...prevFiles, file]);
    }
  };

  const handleRemoveFile = (index) => {
    setMsgFile((prevFiles) => prevFiles.filter((_, i) => i !== index));
  };

  const handleSendMessage = async () => {
    if (!textAreaValue.send_message.trim()) {
      setErrorMessages(true);
      return;
    }

    if (!effectiveRfqToken) {
      openInfoPopup(
        "Unable to Send Message",
        "Missing easyRFQ token for chat.",
      );
      return;
    }

    setSendingMessage(true);
    setErrorMessages(false);

    try {
      const targetRows = selectedVendorRows.filter(
        (item) => item?.thread_token,
      );
      if (!targetRows.length) {
        openInfoPopup(
          "Unable to Send Message",
          "Thread token is missing for the selected vendor.",
        );
        setSendingMessage(false);
        return;
      }

      if (msgFile.length > 0) {
        openInfoPopup(
          "Attachment Not Supported",
          "Buyer easyRFQ chat attachment support is not available in this API flow yet. Please send the text message without attachment.",
        );
        setSendingMessage(false);
        return;
      }

      const responses = await Promise.all(
        targetRows.map((row) =>
          axios.post(
            API_ENDPOINT.send_free_rfq_buyer_chat_message,
            {
              buyer_token: buyerToken,
              thread_token: row.thread_token,
              message: textAreaValue.send_message,
            },
            {
              headers: {
                Authorization: `Bearer ${authToken}`,
              },
            },
          ),
        ),
      );

      handleCloseMessageVendor();
      setBuyerChatThreads((prev) =>
        prev.map((thread) => {
          const matchedResponse = responses.find(
            (response) => response?.data?.thread_token === thread?.thread_token,
          );
          return matchedResponse
            ? {
                ...thread,
                last_message:
                  matchedResponse?.data?.data || thread?.last_message,
              }
            : thread;
        }),
      );
      openInfoPopup(
        "Message Sent Successfully",
        responses?.[0]?.data?.response || "Message sent.",
      );
    } catch (error) {
      console.error("Error sending vendor message:", error);
      openInfoPopup(
        "Unable to Send Message",
        (error?.response?.data && typeof error.response.data === "object"
          ? Object.values(error.response.data)[0]
          : null) ||
          error?.message ||
          "Please try again.",
      );
    } finally {
      setSendingMessage(false);
    }
  };

  const openRequote = () => {
    setActionMode("requote");
    setSelectedQuoteTokens([]);
    setSelectedQuoteRows([]);
    setQuoteSelectionKey((prev) => prev + 1);
    scrollToSection(vendorQuoteSectionRef);
  };

  const submitRequote = async () => {
    if (!effectiveRfqToken) {
      openInfoPopup(
        "Unable to Request Revised Quote",
        "Missing easyRFQ token.",
      );
      return;
    }

    if (!selectedQuoteTokens.length) {
      openInfoPopup(
        "Select Supplier Quotes",
        "Please select at least one Supplier row to request a revised quote.",
      );
      return;
    }

    const formData = new FormData();
    formData.append("rfq_token", effectiveRfqToken);
    formData.append("quotation_token", selectedQuoteTokens.join(","));

    try {
      setVendorQuotationLoading(true);
      const response = await axios.post(API_ENDPOINT.free_rfq_resubmit_request_email, formData, {
        headers: {
          Authorization: `Bearer ${authToken}`,
        },
      });

      resetActionMode();
      openInfoPopup(
        "Resubmission Request Sent",
        (response?.data && typeof response.data === "object"
          ? Object.values(response.data)[0]
          : null) || "Request sent successfully.",
      );
    } catch (error) {
      console.error("Error requesting revised quote:", error);
      openInfoPopup(
        "Unable to Request Revised Quote",
        (error?.response?.data && typeof error.response.data === "object"
          ? Object.values(error.response.data)[0]
          : null) ||
          error?.message ||
          "Please try again.",
      );
    } finally {
      setVendorQuotationLoading(false);
    }
  };

  const openViewQuote = () => {
    setActionMode("quote");
    setSelectedQuoteTokens([]);
    setSelectedQuoteRows([]);
    setQuoteSelectionKey((prev) => prev + 1);
    scrollToSection(vendorQuoteSectionRef);
  };

  const handleViewSelectedQuotes = () => {
    if (!selectedQuoteRows.length) {
      openInfoPopup(
        "Select Supplier Quotes",
        "Please select at least one Supplier row to view the quote details.",
      );
      return;
    }

    setOpenViewQuotesPopup(true);
    setActionMode("");
    setQuoteSelectionKey((prev) => prev + 1);
  };

  const buildTimelineMessages = (rows) => {
  const list = Array.isArray(rows) ? rows : [];

  return list
    .map((msg) => {
      const senderType = String(msg?.sender_type || "").toLowerCase();

      const attachmentUrl = msg?.attachment_url || msg?.attachment || "";
      const attachmentName = msg?.attachment_name || "";

      return {
        id:
          msg?.message_token ||
          msg?.token ||
          msg?.id ||
          `${msg?.created_at || msg?.created_date || ""}-${msg?.message || ""}`,
        rfq: msg?.rfq,
        message: msg?.message,
        sender_type: senderType,
        sender_role:
          msg?.sender_role ||
          (senderType === "vendor" ? "vendor contact" : "buyer"),
        receiver_role:
          msg?.receiver_role ||
          (senderType === "vendor" ? "buyer" : "vendor contact"),
        sender_token: msg?.sender || msg?.sender_token,
        receiver_token: msg?.receiver || msg?.receiver_token,
        created: parseTimelineDate(
          msg?.created_at || msg?.created_date || msg?.created || "",
        ),
        created_raw: msg?.created_at || msg?.created_date || msg?.created || "",
        status: msg?.message_type === "system generated",

        document: attachmentUrl
          ? [
              {
                url: getCloudFrontFileUrl(attachmentUrl),
                name:
                  attachmentName ||
                  String(attachmentUrl).split("/").pop() ||
                  "Attachment",
              },
            ]
          : Array.isArray(msg?.document)
            ? msg.document
                .map((doc, index) => normalizeChatAttachmentEntry(doc, index))
                .filter(Boolean)
            : [],
      };
    })
    .sort((a, b) => a.created - b.created);
};

  const fetchVendorTimeline = async (rowData) => {
    if (!effectiveRfqToken) {
      setTimelineError("Missing easyRFQ token for vendor chat.");
      setViewTimeLineData([]);
      return;
    }

    if (!buyerToken) {
      setTimelineError("Buyer token is not available for this supplier.");
      setViewTimeLineData([]);
      return;
    }

    setTimelineLoading(true);
    setTimelineError("");

    try {
      const threadResponse = await axios.post(
        API_ENDPOINT.get_free_rfq_buyer_chat_threads,
        {
          buyer_token: buyerToken,
          rfq_token: effectiveRfqToken,
        },
        {
          headers: {
            Authorization: `Bearer ${authToken}`,
          },
        },
      );

      const threadResults = Array.isArray(threadResponse?.data?.results)
        ? threadResponse.data.results
        : [];
      setBuyerChatThreads(threadResults);

      const matchedThread =
        threadResults.find(
          (thread) =>
            String(thread?.thread_token || "").trim() ===
            String(rowData?.thread_token || "").trim(),
        ) ||
        threadResults.find(
          (thread) =>
            String(thread?.participant_token || "").trim() ===
            String(rowData?.participant_token || "").trim(),
        ) ||
        threadResults.find(
          (thread) =>
            String(thread?.vendor_email || "")
              .trim()
              .toLowerCase() ===
            String(rowData?.company_official_email || "")
              .trim()
              .toLowerCase(),
        );

      if (!matchedThread) {
        setTimelineError("No chat thread found for this supplier.");
        setViewTimeLineData([]);
        setChatClosed(false);
        return;
      }

      const timelineRows = buildTimelineMessages(
        Array.isArray(matchedThread?.messages) &&
          matchedThread.messages.length > 0
          ? matchedThread.messages
          : matchedThread?.last_message
            ? [matchedThread.last_message]
            : [],
      );
      setViewTimeLineData(timelineRows);
      setTimeLineVendorData({
        ...rowData,
        thread_token: matchedThread?.thread_token,
        participant_token: matchedThread?.participant_token,
        last_message: matchedThread?.last_message,
      });
      setChatClosed(Boolean(matchedThread?.chat_is_closed));
    } catch (error) {
      console.error("Error fetching timeline data:", error);
      setTimelineError(
        (error?.response?.data && typeof error.response.data === "object"
          ? Object.values(error.response.data)[0]
          : null) ||
          error?.message ||
          "Something went wrong while loading the vendor chat.",
      );
      setViewTimeLineData([]);
    } finally {
      setTimelineLoading(false);
    }
  };

  const openViewTimeLineModal = async (_, rowData) => {
    setTimeLineVendorData(rowData);
    setViewTimeLine(true);
    await fetchVendorTimeline(rowData);
  };

  const closeTimeLineView = () => {
    setViewTimeLine(false);
    setTimeLineVendorData(null);
    setViewTimeLineData([]);
    setTimelineError("");
  };

  const chatStateChange = async () => {
    const vendorToken =
      timeLineVendorData?.vendor_token || timeLineVendorData?.token;
    if (!vendorToken || !effectiveRfqToken) {
      openInfoPopup(
        "Unable to Change Chat State",
        "Required chat data is missing.",
      );
      return;
    }

    setChatStatusLoading(true);
    try {
      await axios.post(
        API_ENDPOINT.rfq_chat_box,
        {
          disable: !chatClosed,
          rfq_token: effectiveRfqToken,
          vendor_token: vendorToken,
        },
        {
          headers: {
            Authorization: `Bearer ${authToken}`,
          },
        },
      );

      setChatClosed((prev) => !prev);
      await fetchVendorTimeline(timeLineVendorData);
    } catch (error) {
      console.error("Error updating chat state:", error);
      openInfoPopup(
        "Unable to Change Chat State",
        (error?.response?.data && typeof error.response.data === "object"
          ? Object.values(error.response.data)[0]
          : null) ||
          error?.message ||
          "Please try again.",
      );
    } finally {
      setChatStatusLoading(false);
    }
  };

  const handleTimelineDownload = () => {
    const attachments = timelineDocuments.flatMap((item) =>
      Array.isArray(item?.document) ? item.document : [],
    );

    if (!attachments.length) {
      openInfoPopup(
        "No Documents",
        "There are no uploaded documents available for download.",
      );
      return;
    }

    attachments.forEach((doc) => {
      if (doc?.url) {
        window.open(doc.url, "_blank", "noopener,noreferrer");
      }
    });
  };

  const renderTimelineCards = (entries) => {
    const itemsList = Array.isArray(entries) ? entries : [];

    if (!itemsList.length) {
      return (
        <div className={classes.emptyAbovePagination}>
          No supplier chat messages found for this easyRFQ.
        </div>
      );
    }

    return (
      <div className={classes.threadMessageList}>
        {itemsList.map((item) => {
          const senderType = String(item?.sender_type || "").toLowerCase();
          const isBuyer = senderType === "buyer";

          const senderName = isBuyer
            ? freeRfq?.buyer_contact?.company_name ||
              freeRfq?.buyer_company_name ||
              "Buyer"
            : timeLineVendorData?.company_name ||
              timeLineVendorData?.first_name ||
              timeLineVendorData?.company_official_email ||
              "Supplier";

          const senderRole = isBuyer ? "Buyer" : "Supplier";

          return (
            <div key={item.id} className={classes.threadMessageRow}>
              <div
                className={`${classes.threadSenderCard} ${
                  isBuyer ? classes.buyerSenderCard : classes.supplierSenderCard
                }`}
              >
                <div>{senderName}</div>
                <span>{senderRole}</span>
              </div>

              <div className={classes.threadMessageContent}>
                <div className={classes.threadMessageTitle}>Message</div>

                <div className={classes.threadMessageText}>
                  {item.message || "-"}
                </div>

                <div className={classes.threadMessageDate}>
                  {formatDisplayDateTime(item.created_raw)}
                </div>

              {Array.isArray(item.document) && item.document.length > 0 ? (
  <div className={classes.threadMessageAttachments}>
    {item.document.map((doc, index) => {
      const normalized = normalizeChatAttachmentEntry(doc, index);
      if (!normalized?.url) return null;

      return (
        <a
          key={`${normalized.url}-${index}`}
          href={normalized.url}
          target="_blank"
          rel="noreferrer"
          className={classes.chatDocumentLink}
          title={normalized.url}
        >
          {normalized.name}
        </a>
      );
    })}
  </div>
) : null}
              </div>
            </div>
          );
        })}
      </div>
    );
  };

  const vendorSelectorHeading = (
    <HeaderTitle
      titleText="Message Supplier"
      customclass={classes.heading_pop_pup}
    />
  );

  const vendorSelectorChildren = [
    <>
      <p className={classes.heading_sub_text}>
        Select one or more suppliers to send a message.
      </p>
      <DataTable
        headings={headingMessage}
        tableData={messageSelectionTableData}
        tableSize="100%"
        errorMessage=""
        isLoading={vendorQuotationLoading}
        selection={true}
        customClass={classes.fixedHeader}
        selectedRowsList={handleVendorSelection}
        checkboxSelection={true}
        sendMessage={sendMessage}
      />
      {sendErrorMsg ? (
        <p className={classes.error_msg}>Please select the vendors</p>
      ) : null}
    </>,
  ];

  const vendorSelectorActions = [
    <DButtonOutlined
      key="cancel"
      label="Cancel"
      onClick={handleCloseMessageVendor}
      btnWidth="17rem"
      btnHeight="4rem"
      buyerBtn={true}
    />,
    <DButton
      key="continue"
      label="Continue"
      btnWidth="17rem"
      btnHeight="4rem"
      buyerBtn={true}
      onClick={handleContinueMessage}
    />,
  ];

  const vendorMessageHeading = (
    <HeaderTitle
      titleText="Send Message"
      customclass={classes.heading_pop_pup}
    />
  );

  const vendorMessageChildren = [
    <div key="message-box">
      <p className={classes.heading_sub_text}>Please enter your message</p>
      <TextArea
        maxLength="2000"
        style={{ width: "100%" }}
        title="send_message"
        name="send_message"
        rows={7}
        value={textAreaValue.send_message}
        placeholder="Type your message here..."
        onChange={handleWordCount}
      />
      {charsLeft === 2000 ? (
        <p className={classes.error_msg}>
          Message should be less than 2000 characters only
        </p>
      ) : null}
      {errorMessages ? (
        <p className={classes.error_msg}>Please fill above message field</p>
      ) : null}
      <Attachment
        msg_file={msgFile}
        handleFileChange={handleFileChange}
        handleRemoveFile={handleRemoveFile}
      />
    </div>,
  ];

  const vendorMessageActions = [
    <DButtonOutlined
      key="cancel-compose"
      label="Cancel"
      onClick={handleCloseMessageVendor}
      btnWidth="17rem"
      btnHeight="4rem"
      buyerBtn={true}
    />,
    <DButton
      key="send-message"
      label={sendingMessage ? "Sending" : "Send Message"}
      btnWidth="17rem"
      btnHeight="4rem"
      buyerBtn={true}
      loadingState={sendingMessage}
      onClick={handleSendMessage}
    />,
  ];

  const renderMobileField = (label, value, accent = false) => (
    <div className={classes.mobileCardField} key={label}>
      <span className={classes.mobileCardLabel}>{label}</span>
      <span
        className={`${classes.mobileCardValue} ${
          accent ? classes.mobileCardValueAccent : ""
        }`}
      >
        {value ?? "-"}
      </span>
    </div>
  );

  return (
    <div className={classes.users_tab_wrapper}>
      <div
        className={classes.users_topHeader}
        style={{
          alignItems: "center",
          padding: "0 1.5rem",
          marginBottom: "1rem",
        }}
      >
        <div className={classes.lhs} style={{ marginLeft: 0 }}>
          <HeaderTitle
            titleText={viewTimeLine ? "RFQ Events" : "easyRFQ Details"}
            customclass={classes.dashHead}
          />
        </div>
        <div
          style={{
            display: "flex",
            justifyContent: "flex-end",
            flexShrink: 0,
            gap: "12px",
          }}
        >
          {viewTimeLine ? (
            <DButton
              label="Back"
              btnHeight="4rem"
              buyerBtn={true}
              onClick={closeTimeLineView}
            />
          ) : (
            <DButton
              label="Back"
              btnHeight="4rem"
              buyerBtn={true}
              onClick={() => navigate("/Buyer_dashboard/FreeRFQ")}
            />
          )}
        </div>
      </div>

      <div className={classes.easyRfqDetailsShell}>
        {loading ? (
          <Typography>Loading...</Typography>
        ) : !freeRfq ? (
          <Typography>No RFQ found.</Typography>
        ) : viewTimeLine ? (
          <div className={classes.tabsScreens}>
            <div className={classes.width__100}>
              <h1 className={classes.view__time_line_titel}>
                RFQ Transaction Details
              </h1>
              <div className={classes.view__time_line}>
                {[
                  ["RFQ ID", freeRfq?.rfq_code || "-"],
                  ["Bid Status", freeRfq?.bid_status || "-"],
                  [
                    "Buyer Company",
                    freeRfq?.buyer_contact?.company_name ||
                      freeRfq?.buyer_company_name ||
                      "-",
                  ],
                  [
                    "Supplier Email",
                    timeLineVendorData?.company_official_email || "-",
                  ],
                ].map(([label, value]) => (
                  <div key={label} style={{ flex: "1 1 220px", minWidth: 0 }}>
                    <Typography
                      sx={{
                        fontFamily: "gilroyMedium",
                        fontSize: "14px",
                        color: "#64748b",
                        mb: 0.5,
                      }}
                    >
                      {label}
                    </Typography>
                    <Typography
                      sx={{
                        fontFamily: "gilroyBold",
                        fontSize: "14px",
                        color: "#0f172a",
                        wordBreak: "break-word",
                      }}
                    >
                      {String(value ?? "-")}
                    </Typography>
                  </div>
                ))}

                <div className={classes.chatActionBntWrapper}>
                  <DButton
                    label="DOWNLOAD"
                    btnHeight="4rem"
                    btnWidth="17rem"
                    buyerBtn={true}
                    onClick={handleTimelineDownload}
                  />
                </div>
              </div>

              <div className={classes.timelineContentLayout}>
                <div className={classes.timelineMessagesPanel}>
                  {timelineLoading ? (
                    <Typography
                      sx={{ fontFamily: "gilroyMedium", fontSize: "14px" }}
                    >
                      Loading supplier chat...
                    </Typography>
                  ) : timelineError ? (
                    <div className={classes.emptyAbovePagination}>
                      {timelineError}
                    </div>
                  ) : (
                    renderTimelineCards(viewTimeLineData)
                  )}
                </div>

                <div className={classes.timelineDocsPanel}>
                  <div className={classes.timelineDocsHeader}>
                    Uploaded Documents
                  </div>

                  <div className={classes.timelineDocsContent}>
                   {timelineDocuments.length > 0 ? (
  timelineDocuments.map((entry, index) => {
    const file = entry?.document?.[0];
    if (!file?.url) return null;

    return (
      <a
        key={`${file.url}-${index}`}
        href={file.url}
        target="_blank"
        rel="noreferrer"
        className={classes.timelineDocumentLink}
        title={file.url}
      >
        {file.name || `Attachment ${index + 1}`}
      </a>
    );
  })
) : (
  <div className={classes.noDocsText}>
    No uploaded documents found.
  </div>
)}
                  </div>
                </div>
              </div>
            </div>
          </div>
        ) : (
          <>
            <Box
              className={classes.easyRfqOverviewGrid}
              sx={{
                display: "grid",
                gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))",
                gap: { xs: 1.25, md: 1.5, lg: 2 },
                mb: 2.5,
                width: "100%",
              }}
            >
              {[
                ["RFQ ID", freeRfq?.rfq_code],
                ["Bid Start Date", formatDateOnly(freeRfq?.bid_from_date)],
                ["Bid End Date", formatDateOnly(freeRfq?.bid_to_date)],
                ["Bid Time", formatTimeOnly(freeRfq?.bid_time)],
                ["Bid Status", freeRfq?.bid_status],
                [
                  "Buyer Company",
                  freeRfq?.buyer_contact?.company_name ||
                    freeRfq?.buyer_company_name,
                ],
                [
                  "Buyer Email",
                  freeRfq?.buyer_contact?.email || freeRfq?.buyer_email,
                ],
                [
                  "Buyer Mobile",
                  mobileNumber || "-",
                ],
                ["Shipping Address 1", freeRfq?.shipping_address1],
                ["Shipping Address 2", freeRfq?.shipping_address2],
                ["City", freeRfq?.city],
                ["State", freeRfq?.state],
                ["Country", freeRfq?.country],
                ["Pincode", freeRfq?.pincode],
              ].map(([label, value]) => (
                <Box
                  key={label}
                  className={classes.easyRfqOverviewCard}
                  sx={{
                    minWidth: 0,
                    p: 1.5,
                    borderRadius: "12px",
                    border: "1px solid #e2e8f0",
                    background: "#fff",
                    height: "100%",
                  }}
                >
                  <Typography
                    sx={{
                      fontSize: 15,
                      color: "#0f172a",
                      fontFamily: "gilroyBold",
                      lineHeight: 1.4,
                      mb: 0.5,
                    }}
                  >
                    {label}
                  </Typography>
                  <Typography
                    sx={{
                      fontFamily: "gilroyRegular",
                      fontSize: 14,
                      wordBreak: "break-word",
                      lineHeight: 1.5,
                      color: "#0f172a",
                    }}
                  >
                    {String(value ?? "-")}
                  </Typography>
                </Box>
              ))}
            </Box>

            <Typography sx={{ fontFamily: "gilroyBold", fontSize: 16, mb: 1 }}>
              RFQ Items
            </Typography>
            {isMobileView ? (
              <div className={classes.mobileCardList}>
                {items.length > 0 ? (
                  items.map((item, idx) => (
                    <div
                      key={`${item?.itemCode || item?.itemName || item?.item_name || "item"}-${idx}`}
                      className={classes.mobileDataCard}
                    >
                      <div className={classes.mobileCardHeader}>
                        <div>
                          <div className={classes.mobileCardTitle}>
                            {item?.itemName || item?.item_name || `Item ${idx + 1}`}
                          </div>
                          <div className={classes.mobileCardSubtitle}>
                            {item?.itemCode || item?.item_code || "No item code"}
                          </div>
                        </div>
                      </div>

                      <div className={classes.mobileCardGrid}>
                        {renderMobileField(
                          "HSN",
                          item?.hsnCode || item?.hsn_code || "-",
                        )}
                        {renderMobileField("Quantity", item?.quantity ?? "-")}
                        {renderMobileField("Unit", item?.unit || item?.uom || "-")}
                        {renderMobileField(
                          "Expense Type",
                          item?.expense_type || "-",
                        )}
                      </div>

                      <div className={classes.mobileCardSection}>
                        <div className={classes.mobileCardLabel}>Description</div>
                        <div className={classes.mobileDescriptionValue}>
                          {renderDescriptionCell(
                            item?.description,
                            `rfq-item-${idx}`,
                          )}
                        </div>
                      </div>
                    </div>
                  ))
                ) : (
                  <div className={classes.mobileEmptyCard}>No RFQ items found.</div>
                )}
              </div>
            ) : (
              <div className={classes.easyRfqItemsTableWrap}>
                <table
                  style={{
                    width: "100%",
                    borderCollapse: "collapse",
                    minWidth: 1200,
                  }}
                >
                  <thead style={{ background: "#e2e8f0" }}>
                    <tr>
                      {[
                        "Item Name",
                        "Item Code",
                        "HSN",
                        "Quantity",
                        "Unit",
                        "Description",
                        "Expense Type",
                      ].map((heading) => (
                        <th
                          key={heading}
                          style={{
                            padding: 10,
                            textAlign: "left",
                            fontSize: 14,
                            whiteSpace: "nowrap",
                          }}
                        >
                          {heading}
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {items.length > 0 ? (
                      items.map((item, idx) => (
                        <tr
                          key={`${item?.itemCode || item?.itemName || item?.item_name || "item"}-${idx}`}
                        >
                          <td
                            style={{
                              padding: 10,
                              borderTop: "1px solid #e2e8f0",
                            }}
                          >
                            {item?.itemName || item?.item_name || "-"}
                          </td>
                          <td
                            style={{
                              padding: 10,
                              borderTop: "1px solid #e2e8f0",
                            }}
                          >
                            {item?.itemCode || item?.item_code || "-"}
                          </td>
                          <td
                            style={{
                              padding: 10,
                              borderTop: "1px solid #e2e8f0",
                            }}
                          >
                            {item?.hsnCode || item?.hsn_code || "-"}
                          </td>
                          <td
                            style={{
                              padding: 10,
                              borderTop: "1px solid #e2e8f0",
                            }}
                          >
                            {item?.quantity ?? "-"}
                          </td>
                          <td
                            style={{
                              padding: 10,
                              borderTop: "1px solid #e2e8f0",
                            }}
                          >
                            {item?.unit_measure || item?.uom || item?.unit || "-"}
                          </td>
                          <td
                            style={{
                              padding: 10,
                              borderTop: "1px solid #e2e8f0",
                              maxWidth: 320,
                            }}
                          >
                            {renderDescriptionCell(
                              item?.description,
                              `rfq-item-${idx}`,
                            )}
                          </td>
                          <td
                            style={{
                              padding: 10,
                              borderTop: "1px solid #e2e8f0",
                            }}
                          >
                            {item?.expense_type || "-"}
                          </td>
                        </tr>
                      ))
                    ) : (
                      <tr>
                        <td
                          colSpan={9}
                          style={{ padding: 14, textAlign: "center" }}
                        >
                          No RFQ items found.
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
            )}

            <div
              className={`${classes.compareSection} ${classes.info}`}
              style={{ marginBottom: "1rem" }}
            >
              <div className={classes.rfqActionButtons}>
                {!actionMode ? (
                  <>
                    <DButton
                      label="Message Supplier"
                      btnHeight="4rem"
                      btnWidth="max-content"
                      buyerBtn={true}
                      customClass={classes.rfqActionBtn}
                      disabled={messageSelectionTableData.length === 0}
                      onClick={() => {
                        scrollToSection(vendorMessageSectionRef);
                        handleOpenMessageVendor();
                      }}
                    />
                    <DButton
                      label="Request Revised Quote"
                      btnHeight="4rem"
                      btnWidth="max-content"
                      buyerBtn={true}
                      customClass={classes.rfqActionBtn}
                      disabled={quoteTableData.length === 0}
                      onClick={openRequote}
                    />
                    <DButton
                      label="View Quotation"
                      btnHeight="4rem"
                      btnWidth="max-content"
                      buyerBtn={true}
                      customClass={classes.rfqActionBtn}
                      disabled={quoteTableData.length === 0}
                      onClick={openViewQuote}
                    />
                  </>
                ) : actionMode === "requote" ? (
                  <>
                    <DButtonOutlined
                      label="Cancel"
                      btnHeight="4rem"
                      btnWidth="max-content"
                      buyerBtn={true}
                      customClass={classes.rfqActionBtn}
                      onClick={resetActionMode}
                    />
                    <DButton
                      label="Submit Requote"
                      btnHeight="4rem"
                      btnWidth="max-content"
                      buyerBtn={true}
                      customClass={classes.rfqActionBtn}
                      loadingState={vendorQuotationLoading}
                      disabled={selectedQuoteTokens.length === 0}
                      onClick={submitRequote}
                    />
                  </>
                ) : (
                  <>
                    <DButtonOutlined
                      label="Cancel"
                      btnHeight="4rem"
                      btnWidth="max-content"
                      buyerBtn={true}
                      customClass={classes.rfqActionBtn}
                      onClick={resetActionMode}
                    />
                    <DButton
                      label="View"
                      btnHeight="4rem"
                      btnWidth="max-content"
                      buyerBtn={true}
                      customClass={classes.rfqActionBtn}
                      disabled={selectedQuoteTokens.length === 0}
                      onClick={handleViewSelectedQuotes}
                    />
                  </>
                )}
              </div>
            </div>

            <div ref={vendorQuoteSectionRef}>
              <Typography
                sx={{ fontFamily: "gilroyBold", fontSize: 16, mb: 1 }}
              >
                Supplier Bid Details
              </Typography>
            </div>

            {isMobileView ? (
              vendorQuotationLoading ? (
                <Typography sx={{ fontFamily: "gilroyMedium", fontSize: 14 }}>
                  Loading supplier quotations...
                </Typography>
              ) : quoteTableData.length > 0 ? (
                <div className={classes.mobileCardList}>
                  {quoteTableData.map((row, index) => {
                    const isSelected = selectedQuoteTokenSet.has(
                      String(row?.token || "").trim(),
                    );

                    return (
                      <div
                        key={row?.token || `quote-card-${index}`}
                        className={`${classes.mobileDataCard} ${
                          isSelected ? classes.mobileDataCardSelected : ""
                        }`}
                      >
                        <div className={classes.mobileCardHeader}>
                          <div>
                            <div className={classes.mobileCardTitle}>
                              {row?.vendor || `Supplier ${index + 1}`}
                            </div>
                            <div className={classes.mobileCardSubtitle}>
                              {row?.vendor_email || "-"}
                            </div>
                          </div>

                          {(actionMode === "requote" || actionMode === "quote") ? (
                            <div className={classes.mobileCheckboxWrap}>
                              <span className={classes.mobileCheckboxLabel}>
                                Select
                              </span>
                              <Checkbox
                                size="small"
                                checked={isSelected}
                                onChange={() => toggleMobileQuoteSelection(row)}
                              />
                            </div>
                          ) : null}
                        </div>

                        <div className={classes.mobileCardGrid}>
                          {renderMobileField("Quantity", row?.quantity)}
                          {renderMobileField("Unit Price", row?.unit_price)}
                          {renderMobileField("Total Price", row?.total_price)}
                          {renderMobileField(
                            "Local Currency",
                            row?.converted_currency,
                          )}
                          {renderMobileField("Quote Iteration", row?.bid_version)}
                          {renderMobileField("Created Date", row?.created_date)}
                        </div>

                        <div className={classes.mobileCardSection}>
                          <div className={classes.mobileCardLabel}>File</div>
                          <div className={classes.mobileCardValue}>
                            {row?.file_url || "-"}
                          </div>
                        </div>

                        <button
                          type="button"
                          className={classes.mobileActionButton}
                          onClick={() => openQuoteItemsModal(row)}
                        >
                          View Items
                        </button>
                      </div>
                    );
                  })}
                </div>
              ) : null
            ) : (
              <DataTable
                key={`quote-table-${actionMode}-${quoteSelectionKey}`}
                headings={quoteTableHeadings}
                tableData={quoteTableData}
                tableSize="100%"
                errorMessage={
                  !vendorQuotationLoading && quoteTableData.length === 0
                    ? "No supplier quotations found."
                    : ""
                }
                isLoading={vendorQuotationLoading}
                selection={actionMode === "requote" || actionMode === "quote"}
                maxBidIterationCount={1}
                customClass={`${classes.dTable} ${classes.compactQuoteTable}`}
                hasBidIteration={true}
                selectedRowsList={handleQuoteSelection}
                checkboxSelection={true}
                sorting={false}
              />
            )}

            {!vendorQuotationLoading &&
            quoteTableData.length === 0 &&
            vendorQuotationTokens.length > 0 ? (
              <div className={classes.emptyAbovePagination}>
                No supplier quotation data found for this easyRFQ.
              </div>
            ) : null}

            <div ref={vendorMessageSectionRef} style={{ marginTop: "1.75rem" }}>
              <HeaderTitle
                customclass={classes.tabHeading}
                titleText="Supplier Messages"
              />
            </div>

            {isMobileView ? (
              vendorQuotationLoading && vendorMessagesTableData.length === 0 ? (
                <Typography sx={{ fontFamily: "gilroyMedium", fontSize: 14 }}>
                  Loading supplier contacts...
                </Typography>
              ) : vendorMessagesTableData.length > 0 ? (
                <div className={classes.mobileCardList}>
                  {vendorMessagesTableData.map((row, index) => (
                    <div
                      key={row?.token || row?.vendor_token || `message-card-${index}`}
                      className={classes.mobileDataCard}
                    >
                      <div className={classes.mobileCardHeader}>
                        <div>
                          <div className={classes.mobileCardTitle}>
                            {row?.first_name || row?.company_name || "-"}
                          </div>
                          <div className={classes.mobileCardSubtitle}>
                            {row?.company_official_email || "-"}
                          </div>
                        </div>
                        {Number(row?.unread_count || 0) > 0 ? (
                          <span className={classes.mobileCountBadge}>
                            {row.unread_count} new
                          </span>
                        ) : null}
                      </div>

                      <div className={classes.mobileCardGrid}>
                        {renderMobileField(
                          "Phone",
                          row?.company_phone_number || "-",
                        )}
                        {renderMobileField(
                          "Status",
                          row?.chat_is_closed ? "Closed" : "Open",
                          !row?.chat_is_closed,
                        )}
                      </div>

                      <button
                        type="button"
                        className={classes.mobileActionButton}
                        onClick={() => openViewTimeLineModal(null, row)}
                      >
                        View Timeline
                      </button>
                    </div>
                  ))}
                </div>
              ) : null
            ) : (
              <DataTable
                headings={vendorMessageHeadings}
                tableData={vendorMessagesTableData}
                tableSize="100%"
                isLoading={
                  vendorQuotationLoading && vendorMessagesTableData.length === 0
                }
                errorMessage=""
                selection={false}
                customClass={classes.dTable}
                sorting={false}
                timeline={true}
                viewTimeLineClick={openViewTimeLineModal}
              />
            )}

            {!vendorQuotationLoading && vendorMessagesTableData.length === 0 ? (
              <div className={classes.emptyAbovePagination}>
                No vendor contacts found for this easyRFQ.
              </div>
            ) : null}
          </>
        )}
      </div>

      <Modal open={sendMessage} onClose={handleCloseMessageVendor}>
        <Fade in={sendMessage}>
          <Box>
            <PopupFrame
              poupHeading={vendorSelectorHeading}
              inputChildren={vendorSelectorChildren}
              actionButtons={vendorSelectorActions}
              width="min(92vw, 780px)"
            />
          </Box>
        </Fade>
      </Modal>

      <Modal open={textAreaSendMessage} onClose={handleCloseMessageVendor}>
        <Fade in={textAreaSendMessage}>
          <Box>
            <PopupFrame
              poupHeading={vendorMessageHeading}
              inputChildren={vendorMessageChildren}
              actionButtons={vendorMessageActions}
              width="min(92vw, 760px)"
            />
          </Box>
        </Fade>
      </Modal>

      <Modal open={Boolean(viewQuoteItemsRow)} onClose={closeQuoteItemsModal}>
        <Fade in={Boolean(viewQuoteItemsRow)}>
          <Box
            sx={{
              position: "absolute",
              top: "50%",
              left: "50%",
              transform: "translate(-50%, -50%)",
              width: "min(98vw, 1280px)",
              maxHeight: "86vh",
              overflow: "auto",
              bgcolor: "#fff",
              borderRadius: "14px",
              boxShadow: 24,
              p: 2,
              outline: "none",
            }}
          >
            <Box
              sx={{
                display: "flex",
                alignItems: "flex-start",
                justifyContent: "space-between",
                gap: 2,
                mb: 1.5,
              }}
            >
              <Box>
                <Typography sx={{ fontFamily: "gilroyBold", fontSize: 18 }}>
                  Supplier Bid Items
                </Typography>
                <Typography sx={{ fontFamily: "gilroyMedium", fontSize: 13, color: "#64748b", mt: 0.25 }}>
                  {viewQuoteSupplier}
                </Typography>
              </Box>
              <button
                type="button"
                className={classes.quoteViewIconButton}
                onClick={closeQuoteItemsModal}
                title="Close"
                aria-label="Close item details"
              >
                <Close sx={{ fontSize: 18 }} />
              </button>
            </Box>

            <div className={classes.quoteItemsTableWrap}>
              <table className={classes.quoteItemsTable}>
                <thead>
                  <tr>
                    {[
                      "Item Name",
                      "Actual Item Name",
                      "Part No",
                      "Item Code",
                      "Quantity",
                      "Unit Price",
                      "Discount",
                      "Tax",
                      // "Taxable Amount",
                      "Total Amount",
                      "Description",
                    ].map((heading) => (
                      <th key={heading}>{heading}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {viewQuoteItems.length > 0 ? (
                    viewQuoteItems.map((item, itemIdx) => (
                      <tr key={`${item?.item_code || item?.itemName || "quote-item"}-${itemIdx}`}>
                        <td>{renderReadMoreText(item?.item_name || item?.itemName)}</td>
                        <td>{renderReadMoreText(item?.original_item_name)}</td>
                        <td className={classes.quoteItemsNoWrapCell} title={item?.part_number || ""}>{item?.part_number || "-"}</td>
                        <td className={classes.quoteItemsNoWrapCell} title={item?.item_code || item?.itemCode || ""}>{item?.item_code || item?.itemCode || "-"}</td>
                        <td className={classes.quoteItemsNoWrapCell}>{formatQuantityWithUnit(item)}</td>
                        <td className={classes.quoteItemsNoWrapCell}>{formatOptionalPrice(getItemUnitPrice(item))}</td>
                        <td className={classes.quoteItemsNoWrapCell}>{formatOptionalPrice(item?.discount_amount)}</td>
                        <td className={classes.quoteItemsNoWrapCell}>{formatTaxPercent(getItemTaxPercent(item))}</td>
                        {/* <td className={classes.quoteItemsNoWrapCell}>{formatOptionalPrice(getItemTaxableAmount(item))}</td> */}
                        <td className={classes.quoteItemsNoWrapCell}>{formatOptionalPrice(getItemTotalAmount(item))}</td>
                        <td className={classes.quoteItemsDescriptionCell}>
                          {renderReadMoreText(item?.description)}
                        </td>
                      </tr>
                    ))
                  ) : (
                    <tr>
                      <td colSpan={11} className={classes.quoteItemsEmptyCell}>
                        No quotation items found.
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </Box>
        </Fade>
      </Modal>

      <Modal open={openViewQuotesPopup} onClose={resetActionMode}>
        <Fade in={openViewQuotesPopup}>
          <Box>
            <div className={classes.quotationWrapper}>
              <Close
                onClick={resetActionMode}
                sx={{
                  position: "absolute",
                  right: "30px",
                  top: "20px",
                  fontSize: "35px",
                  cursor: "pointer",
                  borderRadius: "50%",
                  padding: "5px",
                  border: "1px solid #eee",
                }}
              />
              <span>View Quotes</span>
              <div className={classes.pdfWrapper}>
                <div className={classes.buyerPDFView}>
                  <span className={classes.buyerPDFHeading}>
                    Buyer RFQ Details
                  </span>
                  <div
                    style={{
                      background: "#fff",
                      border: "1px solid #dbe3ea",
                      borderRadius: "8px",
                      padding: "16px",
                    }}
                  >
                    <table
                      style={{ width: "100%", borderCollapse: "collapse" }}
                    >
                      <thead style={{ background: "#eef3f7" }}>
                        <tr>
                          {[
                            "Item Name",
                            "Item Code",
                            "HSN",
                            "Quantity",
                            "Unit",
                            "Description",
                            "Expense Type",
                          ].map((heading) => (
                            <th
                              key={heading}
                              style={{
                                padding: "10px",
                                textAlign: "left",
                                fontSize: "13px",
                                borderBottom: "1px solid #dbe3ea",
                                whiteSpace: "nowrap",
                              }}
                            >
                              {heading}
                            </th>
                          ))}
                        </tr>
                      </thead>

                      <tbody>
                        {items.length > 0 ? (
                          items.map((item, idx) => (
                            <tr key={`buyer-rfq-item-${idx}`}>
                              <td
                                style={{
                                  padding: "10px",
                                  borderBottom: "1px solid #edf2f7",
                                }}
                              >
                                {item?.itemName || item?.item_name || "-"}
                              </td>
                              <td
                                style={{
                                  padding: "10px",
                                  borderBottom: "1px solid #edf2f7",
                                }}
                              >
                                {item?.itemCode || item?.item_code || "-"}
                              </td>
                              <td
                                style={{
                                  padding: "10px",
                                  borderBottom: "1px solid #edf2f7",
                                }}
                              >
                                {item?.hsnCode || item?.hsn_code || "-"}
                              </td>
                              <td
                                style={{
                                  padding: "10px",
                                  borderBottom: "1px solid #edf2f7",
                                }}
                              >
                                {item?.quantity ?? "-"}
                              </td>
                              <td
                                style={{
                                  padding: "10px",
                                  borderBottom: "1px solid #edf2f7",
                                }}
                              >
                                {item?.unit || item?.uom || "-"}
                              </td>
                              <td
                                style={{
                                  padding: "10px",
                                  borderBottom: "1px solid #edf2f7",
                                  maxWidth: "260px",
                                  whiteSpace: "pre-wrap",
                                  wordBreak: "break-word",
                                }}
                              >
                                {item?.description || "-"}
                              </td>
                              <td
                                style={{
                                  padding: "10px",
                                  borderBottom: "1px solid #edf2f7",
                                }}
                              >
                                {item?.expense_type || "-"}
                              </td>
                            </tr>
                          ))
                        ) : (
                          <tr>
                            <td
                              colSpan={7}
                              style={{ padding: "14px", textAlign: "center" }}
                            >
                              No RFQ items found.
                            </td>
                          </tr>
                        )}
                      </tbody>
                    </table>

                    {freeRfq?.proposal_document ? (
                      <div style={{ marginTop: "18px" }}>
                        <span className={classes.buyerPDFHeading}>
                          Buyer requirement document
                        </span>
                        <PDFView
                          fileSrc={freeRfq?.proposal_document}
                          customWrapperClass={classes.buyerReqPDF}
                        />
                      </div>
                    ) : null}
                  </div>
                </div>

                <div className={classes.vendorPDFView}>
                  <div className={classes.vendorDocumentsWrapper}>
                    {selectedQuoteDocs.length > 0
                      ? selectedQuoteDocs.map((docs, index) => (
                          <div
                            key={`viewer-doc-${index}`}
                            className={classes.pdfcodeviewr}
                          >
                            <DocViewer documents={docs} />
                          </div>
                        ))
                      : selectedQuoteDataForViewer.map((quote, index) => {
                          const quoteItems = Array.isArray(
                            quote?.quotation_details?.items,
                          )
                            ? quote.quotation_details.items
                            : [];

                          return (
                            <div
                              key={`quote-table-${quote?.vendor_quotation_token || index}`}
                              className={classes.pdfcodeviewr}
                              style={{ background: "#fff", padding: "14px" }}
                            >
                              <div className={classes.vendorName}>
                                {quote?.vendor_name ||
                                  quote?.vendor_contact_name ||
                                  quote?.company_name ||
                                  quote?.vendor_email ||
                                  `Supplier ${index + 1}`}
                              </div>
                              <table
                                className={classes.quoteItemsTable}
                                style={{
                                  width: "100%",
                                  borderCollapse: "collapse",
                                }}
                              >
                                <thead style={{ background: "#eef3f7" }}>
                                  <tr>
                                    {[
                                      "Item Name",
                                      "Actual Item Name",
                                      "Part No",
                                      "Item Code",
                                      "Quantity",
                                      "Unit Price",
                                      "Discount",
                                      "Tax",
                                      "Taxable Amount",
                                      "Total Amount",
                                      "Description",
                                    ].map((heading) => (
                                      <th
                                        key={heading}
                                        style={{
                                          padding: "10px",
                                          textAlign: "left",
                                          fontSize: "13px",
                                          borderBottom: "1px solid #dbe3ea",
                                        }}
                                      >
                                        {heading}
                                      </th>
                                    ))}
                                  </tr>
                                </thead>
                                <tbody>
                                  {quoteItems.length > 0 ? (
                                    quoteItems.map((item, itemIdx) => (
                                      <tr key={`${item?.item_code || itemIdx}`}>
                                        <td
                                          className={classes.quoteItemsReadMoreCell}
                                          style={{
                                            padding: "10px",
                                            borderBottom: "1px solid #edf2f7",
                                          }}
                                        >
                                          {renderReadMoreText(item?.item_name || item?.itemName)}
                                        </td>
                                        <td
                                          className={classes.quoteItemsReadMoreCell}
                                          style={{
                                            padding: "10px",
                                            borderBottom: "1px solid #edf2f7",
                                          }}
                                        >
                                          {renderReadMoreText(item?.original_item_name)}
                                        </td>
                                        <td
                                          className={classes.quoteItemsNoWrapCell}
                                          title={item?.part_number || ""}
                                          style={{
                                            padding: "10px",
                                            borderBottom: "1px solid #edf2f7",
                                          }}
                                        >
                                          {item?.part_number || "-"}
                                        </td>
                                        <td
                                          className={classes.quoteItemsNoWrapCell}
                                          title={item?.item_code || item?.itemCode || ""}
                                          style={{
                                            padding: "10px",
                                            borderBottom: "1px solid #edf2f7",
                                          }}
                                        >
                                          {item?.item_code ||
                                            item?.itemCode ||
                                            "-"}
                                        </td>
                                        <td
                                          className={classes.quoteItemsNoWrapCell}
                                          style={{
                                            padding: "10px",
                                            borderBottom: "1px solid #edf2f7",
                                          }}
                                        >
                                          {formatQuantityWithUnit(item)}
                                        </td>
                                        <td
                                          className={classes.quoteItemsNoWrapCell}
                                          style={{
                                            padding: "10px",
                                            borderBottom: "1px solid #edf2f7",
                                          }}
                                        >
                                          {formatOptionalPrice(getItemUnitPrice(item))}
                                        </td>
                                        <td
                                          className={classes.quoteItemsNoWrapCell}
                                          style={{
                                            padding: "10px",
                                            borderBottom: "1px solid #edf2f7",
                                          }}
                                        >
                                          {formatOptionalPrice(item?.discount_amount)}
                                        </td>
                                        <td
                                          className={classes.quoteItemsNoWrapCell}
                                          style={{
                                            padding: "10px",
                                            borderBottom: "1px solid #edf2f7",
                                          }}
                                        >
                                          {formatTaxPercent(getItemTaxPercent(item))}
                                        </td>
                                        <td
                                          className={classes.quoteItemsNoWrapCell}
                                          style={{
                                            padding: "10px",
                                            borderBottom: "1px solid #edf2f7",
                                          }}
                                        >
                                          {formatOptionalPrice(getItemTaxableAmount(item))}
                                        </td>
                                        <td
                                          className={classes.quoteItemsNoWrapCell}
                                          style={{
                                            padding: "10px",
                                            borderBottom: "1px solid #edf2f7",
                                          }}
                                        >
                                          {formatOptionalPrice(getItemTotalAmount(item))}
                                        </td>
                                        <td
                                          className={classes.quoteItemsDescriptionCell}
                                          style={{
                                            padding: "10px",
                                            borderBottom: "1px solid #edf2f7",
                                          }}
                                        >
                                          {renderReadMoreText(item?.description)}
                                        </td>
                                      </tr>
                                    ))
                                  ) : (
                                    <tr>
                                      <td
                                        colSpan={11}
                                        style={{
                                          padding: "14px",
                                          textAlign: "center",
                                        }}
                                      >
                                        No quotation items found.
                                      </td>
                                    </tr>
                                  )}
                                </tbody>
                              </table>
                            </div>
                          );
                        })}
                  </div>
                </div>
              </div>
            </div>
          </Box>
        </Fade>
      </Modal>

      <Modal open={infoPopup.open} onClose={closeInfoPopup}>
        <Fade in={infoPopup.open}>
          <Box
            sx={{
              position: "absolute",
              top: "50%",
              left: "50%",
              transform: "translate(-50%, -50%)",
              width: "min(92vw, 520px)",
              bgcolor: "#fff",
              borderRadius: "16px",
              boxShadow: 24,
              p: 2.25,
            }}
          >
            <Typography sx={{ fontFamily: "gilroyBold", fontSize: 18, mb: 1 }}>
              {infoPopup.title}
            </Typography>
            <Typography
              sx={{
                fontFamily: "gilroyRegular",
                fontSize: 14,
                color: "#475569",
                mb: 2,
              }}
            >
              {infoPopup.description}
            </Typography>
            <DButton
              label="Okay"
              btnHeight="4rem"
              btnWidth="max-content"
              buyerBtn={true}
              onClick={closeInfoPopup}
            />
          </Box>
        </Fade>
      </Modal>
    </div>
  );
};

export default FreeRFQDetails;
